import React, { useEffect, useState } from "react";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { loginUser } from "./LoginForm.api";
import { useLocation, useNavigate } from "react-router-dom";
import { AxiosError, AxiosResponse } from "axios";
import { useAuth } from "context/auth-context";
import { Modal } from "antd";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { toastrError, toastrSuccess } from "./../../lib/toastr";
import LoadingOverlay from "react-loading-overlay-ts";
import { Link } from "react-router-dom";
const { info } = Modal;
import { ImageComponent } from "./ImageComponent";
import { AuthCard, AuthDivider, AuthLayout } from "./AuthLayout";
import { JoinNowWithGoogle } from "components/JoinNowForm/JoinNowWithGoogle";

const initialState = {
  email: "",
  password: "",
  errors: {
    email: "",
    password: "",
  },
};

type FormState = typeof initialState;
type LoginPayload = Omit<FormState, "errors">;
type ErrorBody = { status: number; message: string };
export const LoginForm = () => {
  const { setAuth } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const toggle = () => {
    setOpen(!open);
  };
  const [formState, setFormState] = useState<FormState>(initialState);

  const virtualWalletConfirmation: any = (location: any) => {
    info({
      title: "Alert",
      icon: <ExclamationCircleFilled />,
      content: t("virtualWallet_confirm.message"),
      onOk() {
        navigate(location.state || "/dashboard", { state: null });
      },
    });
  };

  const authMutation = useMutation(
    (payload: LoginPayload) => {
      return loginUser(payload.email, payload.password, "DEFAULT");
    },
    {
      onSuccess: (response: any) => {
        // 🔴 MFA FLOW
        if (response?.requireMfa) {
          navigate("/mfa-verify", {
            state: {
              signInData: {
                data: {
                  email: response.email,
                },
              },
            },
          });
          return;
        }
        // ✅ NORMAL LOGIN
        if (response?.token) {
          localStorage.setItem("access_token", response.token);
          if (response?.result?.isFlagVirtualWallet) {
            virtualWalletConfirmation(location);
            toastrSuccess(response.message);
            setAuth(response.result.result);
          } else {
            toastrSuccess(response.message);
            setAuth(response.result.result);
            navigate(location.state || "/dashboard");
          }
        }
      },
      onError: (err: AxiosError) => {
        const repsonse = err.response as AxiosResponse<ErrorBody, Record<string, unknown>>;
        if (repsonse.data && repsonse.data.message) {
          toastrError(repsonse.data.message);
        } else {
          toastrError(t("form.join.password"));
        }
      },
    }
  );

  const validation = (formValues: FormState) => {
    setFormState((old) => {
      return { ...old, errors: { ...old.errors, email: "", password: "" } };
    });
    // email validation patterns
    const regexEmail = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

    if (!(formValues.email && regexEmail.test(formValues.email))) {
      setFormState((old) => {
        return { ...old, errors: { ...old.errors, email: t("form.login.emails") } };
      });
      return false;
    }

    //password validation
    if (!(formValues.password.length && formValues.password.length > 4)) {
      setFormState((old) => {
        return { ...old, errors: { ...old.errors, password: t("form.login.Password") } };
      });
      return false;
    }
    return true;
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    const valid = validation(formState);
    if (valid) {
      authMutation.mutate({ email: formState.email, password: formState.password });
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormState({ ...formState, [event.target.name]: event.target.value.trim() });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <LoadingOverlay
        styles={{
          overlay: (base) => ({
            ...base,
            position: "fixed",
          }),
        }}
        active={authMutation.isLoading}
        spinner
        text="Loading..."
      >
        <AuthLayout aside={<ImageComponent />}>
          <AuthCard
            eyebrow="Welcome back"
            title="Log in to your account"
            subtitle="Pick up where you left off and see the surveys waiting for you."
            footer={
              <p>
                Don&apos;t have an account?{" "}
                <Link to={"/join-now"} className="zs-link">
                  Sign up
                </Link>
              </p>
            }
          >
            {/* Google Login */}
            <JoinNowWithGoogle />

            <AuthDivider label="or sign in with email" />

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <label htmlFor="email" className="zs-label zs-label-required">
                  Email
                </label>
                <input
                  type="text"
                  value={formState.email}
                  onChange={handleChange}
                  placeholder="Johndoe@gmail.com"
                  name="email"
                  id="email"
                  aria-invalid={formState.errors.email ? true : undefined}
                  className="zs-input h-11"
                />
                {formState.errors.email && <p className="zs-error">{formState.errors.email}</p>}
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="zs-label zs-label-required">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={open ? "text" : "password"}
                    name="password"
                    id="password"
                    value={formState.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    aria-invalid={formState.errors.password ? true : undefined}
                    className="zs-input h-11 pr-11"
                  />
                  {open ? (
                    <FiEye
                      className="absolute right-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 cursor-pointer text-ink-4 transition-colors hover:text-ink-2"
                      onClick={toggle}
                    />
                  ) : (
                    <FiEyeOff
                      className="absolute right-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 cursor-pointer text-ink-4 transition-colors hover:text-ink-2"
                      onClick={toggle}
                    />
                  )}
                </div>
                {formState.errors.password && <p className="zs-error">{formState.errors.password}</p>}
              </div>

              {/* Forgot password */}
              <div className="-mt-1 text-right">
                <a data-testid="forgetpasswordlink" href="/forgot" className="text-sm zs-link">
                  Forgot password?
                </a>
              </div>

              {/* Submit */}
              <button id="submit-btn" type="submit" className="zs-btn zs-btn-primary zs-btn-lg zs-btn-block">
                Log In
              </button>
            </form>
          </AuthCard>
        </AuthLayout>
      </LoadingOverlay>
    </>
  );
};
